import createImagePlugin from '@draft-js-plugins/image';
import Editor from 'draft-js-plugins-editor';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useFocusMode} from 'contexts/FocusMode';
import FocusModeTimer from 'components/FocusModeTimer';
import checkNetworkStatus from 'utils/functions/checkNetworkStatus';
import {useMobile, useQueryParam} from 'utils/hooks';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {
  fetchWritePreparation,
  finishWritePreparation,
  updateWritePreparation,
} from 'APIs/letrusAPI/writePreparation';
import {WritePreparation as WritePreparationType} from 'APIs/letrusAPI/writePreparation/types';
import {timerSetup} from 'utils/functions/timer';
import {Button, useModals} from 'letrus-ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import useDraftAsTextHighlighter from '../../utils/hooks/useDraftAsTextHighlighter/useDraftAsTextHighlighter';
import styles from './WritePreparation.module.scss';
import WritePreparationSidebar from './WritingPreparationSidebar';
import FinishModal from './modals/FinishModal';
import FirstAccessModal from './modals/FirstAccessModal';

type ViewTypes = 'editor' | 'sidebar';
export type sideBarViews = 'highlight' | 'annotations';

function WritePreparation(): JSX.Element {
  const [openedView, setOpenedView] = useState<ViewTypes>('editor');
  const [openedSidebar, setOpenedSidebar] = useState<sideBarViews>('highlight');

  const writePreparationIDB = useIndexedDB<WritePreparationType>(
    'LocalWritePreparations',
  );

  const navigate = useNavigate();
  const location = useLocation();
  const {isMobile} = useMobile();
  const {trackEvent} = useTrackEvent();
  const {composition_id} = useParams();

  const {firstAccessModal, finishModal} = useModals([
    'firstAccessModal',
    'finishModal',
  ]);

  const learningPathUserInstanceId = useQueryParam().get('trilha') ?? '';
  const {learningPath} = useLearningPathNavigation(learningPathUserInstanceId);

  const {
    isActive,
    remainingSeconds,
    effectiveTimeSeconds,
    isExpired,
    isOffline,
  } = useFocusMode();

  const draftHighlighter = useDraftAsTextHighlighter({
    onHighlightChange: sendHighlightsToAPI,
  });

  const updateWritePreparationMutation = updateWritePreparation();
  const finishWritePreparationMutation = finishWritePreparation();

  const writePreparation = fetchWritePreparation(composition_id, {
    onSuccess: loadWritePreparationOnScreen,
  });

  const eventDataRef = useRef<EventData>({
    composition: {composition_id: Number(composition_id)},
    learningPathUserInstance: {
      learning_path_user_instance_id: 0,
      step_id: 0,
    },
  });

  useEffect(() => {
    eventDataRef.current = {
      composition: {composition_id: Number(composition_id)},
      learningPathUserInstance: {
        learning_path_user_instance_id: learningPath?.id ?? 0,
        step_id:
          learningPath?.steps?.find(
            (step) => step.order === learningPath.step_number,
          )?.id || 0,
      },
    };
  }, [composition_id, learningPath]);

  useEffect(() => {
    const sessionTimer = timerSetup();

    return () => {
      if (!composition_id) {
        return;
      }

      trackEvent('time_spent_on_writing_preparation', {
        ...eventDataRef.current,
        time: {spent_time: sessionTimer.finish()},
      });
    };
  }, []);

  async function compareWithLocalPreparation(
    remotePreparation: WritePreparationType,
  ) {
    const localPreparation = await writePreparationIDB.getItem(composition_id);

    if (!localPreparation || remotePreparation?.id !== localPreparation.id) {
      return remotePreparation;
    }
    const localPreparationDate = new Date(
      localPreparation?.modified_at ?? 0,
    ).getTime();
    const remotePreparationDate = new Date(
      remotePreparation?.modified_at ?? 0,
    ).getTime();
    const latestPreparation =
      remotePreparationDate > localPreparationDate
        ? remotePreparation
        : localPreparation;

    return latestPreparation;
  }

  async function loadWritePreparationOnScreen(
    remotePreparation: WritePreparationType,
  ) {
    const latestsWritePrep = await compareWithLocalPreparation(
      remotePreparation,
    );
    const {first_access, aids_markup_html} = latestsWritePrep;

    draftHighlighter.convertHTMLStringToDraftState(aids_markup_html);

    if (first_access) firstAccessModal?.showModal();
  }

  async function sendWritePreparationToAPI(
    newWritePreparation: Partial<WritePreparationType>,
  ) {
    // Retrieve the most recent version of the WritePreparation stored locally in IndexedDB
    const currentLocalPreparation = await writePreparationIDB.getItem(
      composition_id,
    );

    // Create an object combining the current local version with the new changes received.
    // If there are property conflicts, the values from `newWritePrep` overwrite those from the local version.
    const latestPreparation = {
      ...(currentLocalPreparation ?? {}),
      ...(newWritePreparation as WritePreparationType),
    };

    // Save the combined version in IndexedDB, ensuring that recent changes do not overwrite existing data
    writePreparationIDB.setItem(composition_id, latestPreparation);

    checkNetworkStatus({
      onlineCallback: () => {
        updateWritePreparationMutation.trigger(
          {id: composition_id, ...newWritePreparation},
          {},
        );
      },
    });
  }

  function sendHighlightsToAPI(aids_markup_html: string, markupCount: number) {
    sendWritePreparationToAPI({
      aids_markup_html,
      markup_html_count: markupCount,
    });
  }

  function sendFinishSignalToAPI() {
    finishWritePreparationMutation.trigger(
      {id: composition_id},
      {
        onSuccess: () => updateIDBandRedirect(),
        onError: () => checkNetworkStatusAndRedirect(),
      },
    );
  }

  function updateIDBandRedirect() {
    // updates local version for offline use
    writePreparationIDB.updateItem(composition_id, {
      status: 'finished',
    });

    navigate({
      pathname: `/atividades/escrita/${composition_id}`,
      search: location.search,
    });
  }

  function checkNetworkStatusAndRedirect() {
    checkNetworkStatus({
      onlineCallback: () => {} /* TODO: setup error feedback modal */,
      offlineCallback: () => {
        // the POST goes to the background sync queue, allowing the user to continue
        // the error is dealt with during syncronization
        navigate({
          pathname: `/atividades/escrita/${composition_id}`,
          search: location.search,
        });
      },
    });
  }

  function getViewState(view: ViewTypes): boolean {
    const isViewOpened = openedView === view;

    return isMobile ? isViewOpened : true;
  }

  return (
    <main className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Button
            variant="tertiary"
            aria-label="Voltar"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon="chevron-left" />
          </Button>

          <h1>Preparação à escrita</h1>

          <div className={styles.actions}>
            {isActive && (
              <FocusModeTimer
                remainingSeconds={remainingSeconds}
                effectiveTimeSeconds={effectiveTimeSeconds}
                isExpired={isExpired}
                isInHeader
              />
            )}

            <Button
              onClick={() => finishModal?.showModal()}
              disabled={writePreparation?.isLoading}
            >
              Salvar
            </Button>
          </div>
        </div>
        <p className={styles.headerDescription}>
          Leia os textos abaixo e selecione argumentos e fatos para que você
          possa usá-los:
        </p>
      </div>

      <div className={styles.preparationContainer}>
        <section className={styles.mainArea}>
          <div className={styles.buttonsWrapper}>
            <Button
              variant={
                draftHighlighter.highlightEnabled ? 'primary' : 'secondary'
              }
              theme="blue"
              role="checkbox"
              aria-checked={draftHighlighter.highlightEnabled}
              onClick={() => {
                draftHighlighter.toggleHighlightEnabled();
                setOpenedSidebar('highlight');
              }}
            >
              <FontAwesomeIcon icon="highlighter" />
              Grifar
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                setOpenedSidebar('annotations');
                setOpenedView('sidebar');
              }}
            >
              Adicionar anotação
            </Button>

            {isMobile && (
              <Button
                variant="secondary"
                onClick={() => {
                  setOpenedView(openedView === 'editor' ? 'sidebar' : 'editor');
                }}
              >
                <FontAwesomeIcon icon="file-invoice" />
                Fichário
              </Button>
            )}
          </div>

          {getViewState('editor') && (
            <div className={styles.editorWrapper}>
              <div className={styles.editor}>
                <Editor
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  editorState={draftHighlighter.editorState}
                  onChange={draftHighlighter.addHighlight}
                  customStyleMap={draftHighlighter.styleMap}
                  blockRenderMap={draftHighlighter.blockRenderMap}
                  plugins={[createImagePlugin()]}
                  handleReturn={() => 'handled'}
                  handleKeyCommand={() => 'handled'}
                  handlePastedText={() => 'handled'}
                  handlePastedFiles={() => 'handled'}
                  handleDroppedFiles={() => 'handled'}
                  handleDrop={() => 'handled'}
                  handleBeforeInput={() => 'handled'}
                  readOnly={!draftHighlighter.highlightEnabled || isOffline}
                />
              </div>
            </div>
          )}
        </section>

        {getViewState('sidebar') && (
          <aside className={styles.sidebar}>
            <WritePreparationSidebar
              openedView={openedSidebar}
              setOpenedView={setOpenedSidebar}
              annotations={writePreparation.data?.annotations}
              highlightList={draftHighlighter.highlightList}
              deleteHighlight={draftHighlighter.deleteHighlight}
              onChangeText={(annotations) =>
                sendWritePreparationToAPI({annotations})
              }
            />
          </aside>
        )}
      </div>

      <FirstAccessModal
        id="firstAccessModal"
        onCloseModal={() => firstAccessModal?.close()}
      />

      <FinishModal
        id="finishModal"
        onCloseModal={() => finishModal?.close()}
        onFinishConfirm={() => sendFinishSignalToAPI()}
      />
    </main>
  );
}

export default WritePreparation;
